import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/ui/utils/tailwind';
import { useState } from 'react';

import { MaterialViewer } from './materials/MaterialViewer';
import { UnitMaterials } from './materials/UnitMaterials';
import type { LaunchMaterial } from '../types';
import Text from '@/shared/ui/components/basic/Text';
import { launchReservationGate } from '@/shared/utils/launch-reservation-gate';
import type { LaunchBoardUnit } from '../types';
import { LaunchUnitHoldForm } from './LaunchUnitHoldForm';

interface LaunchUnitActionPanelProps {
  unit: LaunchBoardUnit;
  agencyId: string;
  loading: boolean;
  launchStatus?: string;
  /** Operational+ — mirrors the backend's `manage_launch` permission. */
  canManage: boolean;
  /** Admin/owner only — mirrors the backend's `revert_launch_sale` permission. */
  canRevertSale: boolean;
  /** Compared against `unit.held_by_broker_id` to gate self-release to the actual holder. */
  currentUserEmail?: string;
  onHold: (unitId: string, clientId: string) => void;
  onSelfRelease: (unitId: string) => void;
  onConfirmDeposit: (unitId: string) => void;
  onMarkSold: (unitId: string) => void;
  onForceRelease: (unitId: string) => void;
  onRevertSold: (unitId: string) => void;
  /** Manager decisions on a deposit awaiting review. */
  onValidateDeposit?: (unitId: string) => void;
  onRejectDeposit?: (unitId: string) => void;
  onClose: () => void;
  /**
   * Rendered inside a container that already provides the frame, the title
   * and a close control — the mobile bottom sheet. Drops this panel's own
   * card chrome and header so the two do not stack.
   */
  embedded?: boolean;
}

const MICRO_LABEL = 'text-[length:var(--text-meta)] font-[var(--font-semibold)] tracking-[var(--tracking-wide)]';

interface ActionSpec {
  key: string;
  label: string;
  onClick: () => void;
  variant: 'primary' | 'secondary' | 'danger';
}

/**
 * Renders one action button, styled per the prototype's action hierarchy
 * (seed primary / outline secondary / tinted danger).
 */
function ActionButton({ label, onClick, variant, disabled }: {
  label: string; onClick: () => void; variant: ActionSpec['variant']; disabled?: boolean;
}) {
  const variants: Record<ActionSpec['variant'], string> = {
    primary: 'bg-[var(--seed-base)] text-[var(--text-inverse)] hover:opacity-90',
    secondary: 'border border-[var(--border-subtle)] text-[var(--text-secondary)] hover:bg-(--surface-secondary)',
    danger: 'bg-[var(--launch-danger-bg)] border border-[var(--launch-danger-border)] text-[var(--launch-danger-fg)] hover:brightness-95',
  };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'flex w-full items-center justify-center gap-[var(--space-2)]',
        // 48px: these are pressed in a hurry, standing up, in a sales
        // stand. The old padding-only height came out at 36px.
        'min-h-[var(--space-12)]',
        'rounded-(--radius-md) px-[var(--space-3)] py-[var(--space-2)]',
        'text-[length:var(--text-small)] font-[var(--font-medium)]',
        variants[variant],
        disabled && 'cursor-not-allowed',
      )}
      style={disabled ? { opacity: 'var(--launch-opacity-disabled)' } : undefined}
    >
      {label}
    </button>
  );
}

/**
 * Displays a label-value detail row, hidden when value is empty.
 */
function Detail({ label, value }: { label: string; value: string | null | undefined }) {
  if (!value) return null;
  return (
    <div className="flex justify-between text-[length:var(--text-meta)]">
      <span className="text-[var(--text-tertiary)]">{label}</span>
      <span className="text-[var(--text-primary)] font-[var(--font-medium)]">{value}</span>
    </div>
  );
}

/**
 * Panel showing unit details. Hold form only renders when the launch is open.
 */
export function LaunchUnitActionPanel({
  unit, agencyId, loading, launchStatus, canManage, canRevertSale, currentUserEmail,
  onHold, onSelfRelease, onConfirmDeposit, onMarkSold, onForceRelease, onRevertSold, onClose,
  onValidateDeposit, onRejectDeposit,
  embedded = false,
}: LaunchUnitActionPanelProps) {
  const { t } = useTranslation('features/launches');
  const [viewing, setViewing] = useState<LaunchMaterial | null>(null);
  const gate = launchReservationGate(launchStatus);
  const isHoldOwner = Boolean(currentUserEmail) && unit.held_by_broker_id === currentUserEmail;
  const priceStr = unit.list_price
    ? unit.list_price.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    : null;

  const status = unit.allocation_status;
  const actions: ActionSpec[] = [];
  if (gate.allowed) {
    if (status === 'held') {
      if (isHoldOwner) {
        actions.push({ key: 'release', label: t('actions.selfRelease'), onClick: () => onSelfRelease(unit.unit_id), variant: 'secondary' });
      }
      if (canManage) {
        actions.push({ key: 'confirm', label: t('actions.confirmDeposit'), onClick: () => onConfirmDeposit(unit.unit_id), variant: 'primary' });
        actions.push({ key: 'force-release', label: t('actions.forceRelease'), onClick: () => onForceRelease(unit.unit_id), variant: 'danger' });
      }
    }
    // A deposit awaiting review: the manager decides, and nobody else can
    // act on the unit until they do.
    if (status === 'deposit_pending' && canManage) {
      if (onValidateDeposit) {
        actions.push({
          key: 'validate-deposit',
          label: t('actions.validateDeposit'),
          onClick: () => onValidateDeposit(unit.unit_id),
          variant: 'primary',
        });
      }
      if (onRejectDeposit) {
        actions.push({
          key: 'reject-deposit',
          label: t('actions.rejectDeposit'),
          onClick: () => onRejectDeposit(unit.unit_id),
          variant: 'danger',
        });
      }
    }
    if (status === 'reserved_with_deposit' && canManage) {
      actions.push({ key: 'sold', label: t('actions.markSold'), onClick: () => onMarkSold(unit.unit_id), variant: 'primary' });
      actions.push({ key: 'force-release', label: t('actions.forceRelease'), onClick: () => onForceRelease(unit.unit_id), variant: 'danger' });
    }
    if (status === 'sold' && canRevertSale) {
      actions.push({ key: 'revert', label: t('actions.revertSold'), onClick: () => onRevertSold(unit.unit_id), variant: 'danger' });
    }
  }
  const showHoldForm = gate.allowed && status === 'available';
  const showNoActionsBox = gate.allowed && !showHoldForm && actions.length === 0;

  let noActionsReason: string;
  if (launchStatus === 'draft') {
    noActionsReason = t('board.unitDetails.noActionsDraft');
  } else if (launchStatus === 'closed') {
    noActionsReason = t('board.unitDetails.noActionsClosed');
  } else if (status === 'held' && !isHoldOwner && !canManage) {
    noActionsReason = t('board.unitDetails.noActionsHeldByOther', { holder: unit.held_by_broker_id ?? '' });
  } else if (status === 'sold' && !canRevertSale) {
    noActionsReason = t('board.unitDetails.noActionsRevertRestricted');
  } else {
    noActionsReason = t('board.unitDetails.noActionsGeneric');
  }

  const holderCaption = status === 'sold'
    ? t('board.unitDetails.holderCaptionSold')
    : status === 'reserved_with_deposit'
      ? t('board.unitDetails.holderCaptionReserved')
      : t('board.unitDetails.holderCaptionHeld');

  return (
    <section
      className={cn(
        'launch-scope flex flex-col gap-[var(--space-4)]',
        !embedded
          && 'rounded-(--radius-lg) border border-(--border-subtle) bg-(--surface-primary) p-[var(--space-4)] shadow-[var(--shadow-sm)]',
      )}
    >
      {!embedded && (
        <>
          <div className="flex items-center justify-between border-b border-(--border-subtle) pb-[var(--space-3)]">
            <Text as="span" className={cn(MICRO_LABEL, 'text-(--text-tertiary)')}>
              {t('board.unitDetails.heading')}
            </Text>
            <button
              type="button"
              onClick={onClose}
              className="flex h-[var(--space-12)] w-[var(--space-12)] items-center justify-center rounded-(--radius-sm) text-(--text-tertiary) hover:bg-(--surface-secondary) hover:text-(--text-primary)"
              aria-label={t('confirm.cancel')}
            >
              ✕
            </button>
          </div>

          <Text as="span" className="text-[length:var(--text-body-sm)] font-semibold text-(--text-primary) truncate">
            {unit.label}
          </Text>
        </>
      )}

      <div className="flex flex-col gap-[var(--space-2)]">
        <Detail label={t('board.towerLabel')} value={unit.tower} />
        <Detail label={t('board.unitDetails.floor')} value={unit.floor} />
        <Detail label={t('board.unitDetails.typology')} value={unit.typology} />
        <Detail label={t('board.unitDetails.price')} value={priceStr} />
        <Detail label={t('board.unitDetails.status')} value={t(`status.${unit.allocation_status}`)} />
      </div>

      {(unit.held_by_broker_id || unit.held_for_client_id) && (
        <div
          className="flex items-center justify-between gap-[var(--space-2)] rounded-(--radius-md) p-[var(--space-2)]"
          style={{
            background: status === 'held' ? 'var(--launch-status-held-bg)' : 'var(--surface-tertiary)',
            border: `1px solid ${status === 'held' ? 'var(--launch-status-held-border)' : 'var(--border-subtle)'}`,
          }}
        >
          <div className="flex flex-wrap items-center gap-[var(--space-2)]">
            <span className={cn(MICRO_LABEL, 'text-(--text-tertiary)')}>{holderCaption}</span>
            {unit.held_by_broker_id && (
              <span
                className="rounded-(--radius-full) px-[var(--space-2)] py-[var(--space-0-5)] text-[length:var(--text-meta)] font-semibold"
                style={{ background: 'var(--launch-role-broker-bg)', color: 'var(--launch-role-broker-fg)' }}
              >
                {t('board.unitDetails.brokerBadge')}
              </span>
            )}
            <span className="text-[length:var(--text-small)] text-(--text-primary)">
              {unit.held_by_broker_id ?? unit.held_for_client_id}
            </span>
          </div>
        </div>
      )}

      {/* Support, not the point: below the unit's details and above the
          actions, so it is reachable without leaving the board and never
          competes with the button the broker came here to press. */}
      <UnitMaterials
        agencyId={agencyId}
        launchId={unit.launch_id}
        typology={unit.typology}
        onOpen={setViewing}
      />

      <MaterialViewer material={viewing} onClose={() => setViewing(null)} />

      <div className="border-t border-(--border-subtle) pt-[var(--space-3)] flex flex-col gap-[var(--space-2)]">
        {!gate.allowed && (
          <div
            role="status"
            data-testid="reservation-blocked"
            className="rounded-(--radius-md) border p-[var(--space-3)] text-[length:var(--text-small)]"
            style={{ background: 'var(--launch-warning-bg)', borderColor: 'var(--launch-warning-border)', color: 'var(--launch-warning-fg)' }}
          >
            {t(`board.reservationBlocked.${gate.reason}`)}
          </div>
        )}

        {showHoldForm && (
          <LaunchUnitHoldForm agencyId={agencyId} unitId={unit.unit_id} loading={loading} onHold={onHold} />
        )}

        {gate.allowed && actions.length > 0 && (
          <>
            <span className={cn(MICRO_LABEL, 'text-(--text-tertiary)')}>
              {t('board.unitDetails.actionsHeading')}
            </span>
            {actions.map((a) => (
              <ActionButton key={a.key} label={a.label} onClick={a.onClick} variant={a.variant} disabled={loading} />
            ))}
          </>
        )}

        {showNoActionsBox && (
          <div
            className="rounded-(--radius-md) p-[var(--space-3)] text-[length:var(--text-small)]"
            style={{ background: 'var(--surface-tertiary)', border: '1px dashed var(--border-medium)', color: 'var(--text-secondary)' }}
          >
            {noActionsReason}
          </div>
        )}
      </div>
    </section>
  );
}
